/**
 * Format Command Drawer & Floating Panel (Requirement 5).
 * Modern categorized formatting cards for Numbers, Typography, Alignment, Colors, and Borders.
 */

import { el } from './dom.js';
import type { CellStylePatch } from '../core/format/style.js';
import { numberFormat, type NumberFormat } from '../core/format/numberFormat.js';

export interface FormatDrawerOptions {
  onApplyStyle: (patch: CellStylePatch) => void;
  onApplyNumberFormat: (fmt: NumberFormat) => void;
  onMerge: () => void;
  onClose?: () => void;
}

export interface FormatDrawer {
  element: HTMLElement;
  open(): void;
  close(): void;
  isOpen(): boolean;
}

export function createFormatDrawer(options: FormatDrawerOptions): FormatDrawer {
  let isOpen = false;

  const closeBtn = el('button', {
    className: 'rz-drawer-close',
    text: '✕',
    attrs: { 'aria-label': 'Close formatting drawer', title: 'Close' },
  });

  // Section 1: Number Formatting Cards
  const numberCards = el('div', { className: 'rz-format-cards-grid' }, [
    createFormatCard('123', 'Automatic', 'General format', () => options.onApplyNumberFormat(numberFormat({ category: 'general' }))),
    createFormatCard('1,234.56', 'Number', 'Standard number format', () => options.onApplyNumberFormat(numberFormat({ category: 'number', grouping: true, decimals: 2 }))),
    createFormatCard('$ 100.00', 'Currency', 'USD / Local Currency', () => options.onApplyNumberFormat(numberFormat({ category: 'currency', currency: '$', decimals: 2 }))),
    createFormatCard('% 75.0', 'Percentage', 'Multiply by 100 with %', () => options.onApplyNumberFormat(numberFormat({ category: 'percentage', decimals: 2 }))),
    createFormatCard('📅 2026-08', 'Date', 'ISO YYYY-MM-DD', () => options.onApplyNumberFormat(numberFormat({ category: 'date', pattern: 'yyyy-mm-dd' }))),
    createFormatCard('🕒 14:30', 'Time', '24-hour time format', () => options.onApplyNumberFormat(numberFormat({ category: 'time' }))),
    createFormatCard('🔤 Text', 'Plain Text', 'Raw string format', () => options.onApplyNumberFormat(numberFormat({ category: 'text' }))),
  ]);

  // Section 2: Typography & Emphasis
  const typeCards = el('div', { className: 'rz-format-actions-row' }, [
    createActionButton('𝐁 Bold', 'Toggle Bold (Ctrl+B)', () => options.onApplyStyle({ bold: true })),
    createActionButton('𝘐 Italic', 'Toggle Italic (Ctrl+I)', () => options.onApplyStyle({ italic: true })),
    createActionButton('̲U̲ Underline', 'Toggle Underline (Ctrl+U)', () => options.onApplyStyle({ underline: true })),
  ]);

  // Section 3: Alignment & Structure
  const alignCards = el('div', { className: 'rz-format-actions-row' }, [
    createActionButton('⬅ Left', 'Align Left', () => options.onApplyStyle({ hAlign: 'left' })),
    createActionButton('⬌ Center', 'Align Center', () => options.onApplyStyle({ hAlign: 'center' })),
    createActionButton('➡ Right', 'Align Right', () => options.onApplyStyle({ hAlign: 'right' })),
    createActionButton('↵ Wrap Text', 'Wrap long cell contents', () => options.onApplyStyle({ wrap: true })),
    createActionButton('⧉ Merge Cells', 'Merge selected cell block', () => options.onMerge()),
  ]);

  // Section 4: Color Presets
  const colorPresets = [
    { bg: '#fee2e2', text: '#991b1b', name: 'Alert Red' },
    { bg: '#fef3c7', text: '#92400e', name: 'Warning Amber' },
    { bg: '#dcfce7', text: '#166534', name: 'Success Green' },
    { bg: '#e0f2fe', text: '#075985', name: 'Info Cyan' },
    { bg: '#ede9fe', text: '#5b21b6', name: 'Accent Violet' },
    { bg: '#ffffff', text: '#0f172a', name: 'Clear/White' },
  ];

  const colorCards = el('div', { className: 'rz-format-colors-grid' }, 
    colorPresets.map((c) => {
      const btn = el('button', {
        className: 'rz-color-preset-card',
        attrs: { style: `background: ${c.bg}; color: ${c.text}; border-color: ${c.text}33;`, title: c.name },
      }, [
        el('span', { className: 'rz-color-preset-sample', text: 'Aa' }),
        el('span', { className: 'rz-color-preset-name', text: c.name }),
      ]);
      btn.addEventListener('click', () => {
        options.onApplyStyle({ background: c.bg, color: c.text });
      });
      return btn;
    })
  );

  const drawer = el('div', { className: 'rz-format-drawer' }, [
    el('div', { className: 'rz-drawer-header' }, [
      el('div', { className: 'rz-drawer-title-group' }, [
        el('span', { className: 'rz-drawer-icon', text: '🎨' }),
        el('h3', { className: 'rz-drawer-title', text: 'Cell & Range Formatting' }),
      ]),
      closeBtn,
    ]),
    el('div', { className: 'rz-drawer-content' }, [
      el('div', { className: 'rz-drawer-section' }, [
        el('h4', { className: 'rz-section-title', text: 'Number & Type Formatting' }),
        numberCards,
      ]),
      el('div', { className: 'rz-drawer-section' }, [
        el('h4', { className: 'rz-section-title', text: 'Typography & Style' }),
        typeCards,
      ]),
      el('div', { className: 'rz-drawer-section' }, [
        el('h4', { className: 'rz-section-title', text: 'Alignment & Layout' }),
        alignCards,
      ]),
      el('div', { className: 'rz-drawer-section' }, [
        el('h4', { className: 'rz-section-title', text: 'Semantic Theme Presets' }),
        colorCards,
      ]),
    ]),
  ]);

  const overlay = el('div', { className: 'rz-drawer-overlay' }, [drawer]);
  overlay.style.display = 'none';

  function createFormatCard(sample: string, label: string, desc: string, onClick: () => void): HTMLElement {
    const card = el('button', { className: 'rz-format-card' }, [
      el('span', { className: 'rz-format-card-sample', text: sample }),
      el('span', { className: 'rz-format-card-label', text: label }),
      el('span', { className: 'rz-format-card-desc', text: desc }),
    ]);
    card.addEventListener('click', () => {
      onClick();
    });
    return card;
  }

  function createActionButton(label: string, title: string, onClick: () => void): HTMLElement {
    const btn = el('button', { className: 'rz-action-button', text: label, attrs: { title } });
    btn.addEventListener('click', onClick);
    return btn;
  }

  closeBtn.addEventListener('click', () => close());
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  function open(): void {
    isOpen = true;
    overlay.style.display = 'flex';
  }

  function close(): void {
    if (!isOpen) return;
    isOpen = false;
    overlay.style.display = 'none';
    options.onClose?.();
  }

  return {
    element: overlay,
    open,
    close,
    isOpen: () => isOpen,
  };
}
